import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import isObject from 'lodash-es/isObject';
import isString from 'lodash-es/isString';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useIntl } from 'react-intl';
import slugify from 'slugify';
import { v1 } from 'uuid';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import { loadPackage } from '@panneau/core/utils';

var convertToMedia = function convertToMedia(it) {
  // console.log('upload', it);
  var type = it.data.type.split('/')[0];
  var thumbnail = it.transloadit["".concat(type, "_thumbnail")] || null;
  var original = it.transloadit["".concat(type, "_original")] || null;
  return {
    handle: it.id,
    type: type,
    name: it.meta.name,
    mime: it.data.type,
    size: it.data.size,
    url: original !== null ? original.ssl_url || original.url : null,
    thumbnail_url: thumbnail !== null ? thumbnail.ssl_url || thumbnail.url : null,
    metadata: _objectSpread(_objectSpread(_objectSpread({}, original !== null ? original.meta || null : null), it.meta.user ? {
      user: it.meta.user
    } || null : null), {}, {
      filename: it.meta.filename,
      transloadit: it.transloadit.results || null
    })
  };
};
var getTransloaditMediasFromResponse = function getTransloaditMediasFromResponse(response) {
  return response.successful.map(function (it) {
    var transloadit = response.transloadit.find(function (subIt) {
      return subIt.assembly_id === it.transloadit.assembly;
    }) || null;
    var results = transloadit !== null ? transloadit.results || null : null;
    return _objectSpread(_objectSpread({}, it), {}, {
      transloadit: results !== null ? Object.keys(results).reduce(function (map, resultKey) {
        var result = results[resultKey].find(function (itResult) {
          return itResult.name === it.name;
        });
        return result !== null ? _objectSpread(_objectSpread({}, map), {}, _defineProperty({}, resultKey, result)) : map;
      }, null) : null
    });
  }).filter(function (it) {
    return it.transloadit !== null;
  }).map(function (it) {
    return convertToMedia(it);
  });
};

/**
 * Locale loader
 */
var packageCache = null;
var useUppyCore = function useUppyCore() {
  // transport
  var _useState = useState({
      "package": packageCache
    }),
    _useState2 = _slicedToArray(_useState, 2),
    loadedPackage = _useState2[0]["package"],
    setLoadedPackage = _useState2[1];
  useEffect(function () {
    var canceled = false;
    if (loadedPackage !== null) {
      return function () {
        canceled = true;
      };
    }
    loadPackage('@uppy/core', function () {
      return import('@uppy/core');
    }).then(function (_ref) {
      var Uppy = _ref["default"];
      packageCache = Uppy;
      if (!canceled) {
        setLoadedPackage({
          "package": Uppy
        });
      }
    });
    return function () {
      canceled = true;
    };
  }, [loadedPackage, setLoadedPackage]);
  return loadedPackage;
};

/**
 * Locale loader
 */
var packagesCache$2 = {};
var defaultPackagesMap$2 = {
  fr: function fr() {
    return loadPackage('@uppy/locales/lib/fr_FR', function () {
      return import('@uppy/locales/lib/fr_FR');
    });
  },
  en: function en() {
    return loadPackage('@uppy/locales/lib/en_US', function () {
      return import('@uppy/locales/lib/en_US');
    });
  }
};
var useUppyLocale = function useUppyLocale(locale) {
  var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
    _ref$packagesMap = _ref.packagesMap,
    packagesMap = _ref$packagesMap === void 0 ? defaultPackagesMap$2 : _ref$packagesMap;
  var _useState = useState({
      "package": packagesCache$2[locale] || null
    }),
    _useState2 = _slicedToArray(_useState, 2),
    loadedPackage = _useState2[0]["package"],
    setLoadedPackage = _useState2[1];
  var packageLoader = packagesMap[locale] || null;
  useEffect(function () {
    var canceled = false;
    if (loadedPackage !== null || packageLoader === null) {
      return function () {
        canceled = true;
      };
    }
    packageLoader().then(function (_ref2) {
      var dep = _ref2["default"];
      // packagesCache[locale] = dep;
      if (!canceled) {
        setLoadedPackage({
          "package": dep
        });
      }
    });
    return function () {
      canceled = true;
    };
  }, [packageLoader, loadedPackage, setLoadedPackage]);
  return loadedPackage;
};

var _excluded$2 = ["default"];

/**
 * Locale loader
 */
var packagesCache$1 = {};
var defaultPackagesMap$1 = {
  webcam: function webcam() {
    return loadPackage('@uppy/webcam', function () {
      return import('@uppy/webcam');
    });
  },
  facebook: function facebook() {
    return loadPackage('@uppy/facebook', function () {
      return import('@uppy/facebook');
    });
  },
  instagram: function instagram() {
    return loadPackage('@uppy/instagram', function () {
      return import('@uppy/instagram');
    });
  },
  dropbox: function dropbox() {
    return loadPackage('@uppy/dropbox', function () {
      return import('@uppy/dropbox');
    });
  },
  'google-drive': function googleDrive() {
    return loadPackage('@uppy/google-drive', function () {
      return import('@uppy/google-drive');
    });
  }
};
var useUppySources = function useUppySources(sources) {
  var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
    _ref$packagesMap = _ref.packagesMap,
    packagesMap = _ref$packagesMap === void 0 ? defaultPackagesMap$1 : _ref$packagesMap;
  // transport
  var _useState = useState({
      packages: sources.reduce(function (map, source) {
        var sourcePackage = packagesCache$1[source] || null;
        if (sourcePackage === null) {
          return map;
        }
        return _objectSpread(_objectSpread({}, map), {}, _defineProperty({}, source, sourcePackage));
      }, null)
    }),
    _useState2 = _slicedToArray(_useState, 2),
    loadedPackages = _useState2[0].packages,
    setLoadedPackages = _useState2[1];
  var sourcesToLoad = useMemo(function () {
    if (loadedPackages === null) {
      return sources;
    }
    var sourcesLoaded = Object.keys(loadedPackages);
    return sources.filter(function (source) {
      return sourcesLoaded.indexOf(source) === -1;
    });
  }, [sources, loadedPackages]);
  useEffect(function () {
    var canceled = false;
    if (sourcesToLoad.length === 0) {
      return function () {
        canceled = true;
      };
    }
    Promise.all(sourcesToLoad.map(function (source) {
      return packagesMap[source] || null;
    }).filter(function (it) {
      return it !== null;
    }).map(function (promise) {
      return promise();
    })).then(function (packagesLoaded) {
      var newLoadedPackages = sourcesToLoad.reduce(function (map, source, index) {
        var _packagesLoaded$index = packagesLoaded[index],
          pack = _packagesLoaded$index["default"],
          others = _objectWithoutProperties(_packagesLoaded$index, _excluded$2);
        return _objectSpread(_objectSpread({}, map), {}, _defineProperty({}, source, Object.keys(others).reduce(function (otherMap, key) {
          otherMap[key] = others[key]; // eslint-disable-line no-param-reassign
          return otherMap;
        }, pack)));
      }, {});
      packagesCache$1 = _objectSpread(_objectSpread({}, packagesCache$1), newLoadedPackages);
      if (!canceled) {
        setLoadedPackages({
          packages: newLoadedPackages
        });
      }
    });
    return function () {
      canceled = true;
    };
  }, [sourcesToLoad, packagesMap, loadedPackages, setLoadedPackages]);
  return sourcesToLoad.length === 0 ? loadedPackages : null;
};

var _excluded$1 = ["default"];

/**
 * Locale loader
 */
var packagesCache = {};
var defaultPackagesMap = {
  transloadit: function transloadit() {
    return loadPackage('@uppy/transloadit', function () {
      return import('@uppy/transloadit');
    });
  },
  tus: function tus() {
    return loadPackage('@uppy/tus', function () {
      return import('@uppy/tus');
    });
  },
  xhr: function xhr() {
    return loadPackage('@uppy/xhr-upload', function () {
      return import('@uppy/xhr-upload');
    });
  }
};
var useUppyTransport = function useUppyTransport(transport) {
  var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
    _ref$packagesMap = _ref.packagesMap,
    packagesMap = _ref$packagesMap === void 0 ? defaultPackagesMap : _ref$packagesMap;
  // transport
  var _useState = useState({
      "package": packagesCache[transport] || null
    }),
    _useState2 = _slicedToArray(_useState, 2),
    loadedPackage = _useState2[0]["package"],
    setLoadedPackage = _useState2[1];
  var packageLoader = packagesMap[transport] || null;
  useEffect(function () {
    var canceled = false;
    if (loadedPackage !== null || packageLoader === null) {
      return function () {
        canceled = true;
      };
    }
    packageLoader().then(function (_ref2) {
      var pack = _ref2["default"],
        others = _objectWithoutProperties(_ref2, _excluded$1);
      var dep = Object.keys(others).reduce(function (map, key) {
        map[key] = others[key]; // eslint-disable-line no-param-reassign
        return map;
      }, pack);
      packagesCache[transport] = dep;
      if (!canceled) {
        setLoadedPackage({
          "package": dep
        });
      }
    });
    return function () {
      canceled = true;
    };
  }, [packageLoader, loadedPackage, setLoadedPackage]);
  return loadedPackage;
};

var _excluded = ["sources"],
  _excluded2 = ["key", "templateId", "waitForEncoding"];
var UppyContext = /*#__PURE__*/React.createContext(null);
var useUppyConfig = function useUppyConfig() {
  var _ref = useContext(UppyContext) || {},
    _ref$transport = _ref.transport,
    transport = _ref$transport === void 0 ? null : _ref$transport,
    _ref$locale = _ref.locale,
    locale = _ref$locale === void 0 ? null : _ref$locale,
    _ref$sources = _ref.sources,
    sources = _ref$sources === void 0 ? null : _ref$sources,
    _ref$transloadit = _ref.transloadit,
    transloadit = _ref$transloadit === void 0 ? null : _ref$transloadit,
    _ref$companion = _ref.companion,
    companion = _ref$companion === void 0 ? null : _ref$companion,
    _ref$tus = _ref.tus,
    tus = _ref$tus === void 0 ? null : _ref$tus,
    _ref$xhr = _ref.xhr,
    xhr = _ref$xhr === void 0 ? null : _ref$xhr;
  return {
    transport: transport,
    locale: locale,
    sources: sources,
    transloadit: transloadit,
    companion: companion,
    tus: tus,
    xhr: xhr
  };
};
var useUppy = function useUppy() {
  var _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    _ref2$onComplete = _ref2.onComplete,
    onComplete = _ref2$onComplete === void 0 ? null : _ref2$onComplete,
    _ref2$onFail = _ref2.onFail,
    onFail = _ref2$onFail === void 0 ? null : _ref2$onFail,
    _ref2$getFileName = _ref2.getFileName,
    getFileName = _ref2$getFileName === void 0 ? function (_ref3) {
      var _ref3$name = _ref3.name,
        name = _ref3$name === void 0 ? '' : _ref3$name,
        _ref3$extension = _ref3.extension,
        extension = _ref3$extension === void 0 ? null : _ref3$extension;
      return "".concat((slugify(name) || '').substring(0, 160)).concat(extension !== null && (name || '').indexOf(extension) === -1 ? ".".concat(extension) : '');
    } : _ref2$getFileName,
    _ref2$getFileNameWith = _ref2.getFileNameWithUUID,
    getFileNameWithUUID = _ref2$getFileNameWith === void 0 ? function (_ref4) {
      var _ref4$extension = _ref4.extension,
        extension = _ref4$extension === void 0 ? null : _ref4$extension;
      return "".concat(v1()).concat(extension !== null ? ".".concat(extension) : '');
    } : _ref2$getFileNameWith,
    _ref2$withUUID = _ref2.withUUID,
    withUUID = _ref2$withUUID === void 0 ? false : _ref2$withUUID,
    _ref2$meta = _ref2.meta,
    meta = _ref2$meta === void 0 ? null : _ref2$meta,
    _ref2$allowMultipleUp = _ref2.allowMultipleUploads,
    allowMultipleUploads = _ref2$allowMultipleUp === void 0 ? false : _ref2$allowMultipleUp,
    _ref2$maxNumberOfFile = _ref2.maxNumberOfFiles,
    maxNumberOfFiles = _ref2$maxNumberOfFile === void 0 ? 1 : _ref2$maxNumberOfFile,
    _ref2$allowedFileType = _ref2.allowedFileTypes,
    allowedFileTypes = _ref2$allowedFileType === void 0 ? null : _ref2$allowedFileType,
    _ref2$autoProceed = _ref2.autoProceed,
    autoProceed = _ref2$autoProceed === void 0 ? false : _ref2$autoProceed,
    _ref2$debug = _ref2.debug,
    debug = _ref2$debug === void 0 ? false : _ref2$debug;
  var _ref5 = useContext(UppyContext) || null,
    buildUppy = _ref5.buildUppy,
    transport = _ref5.transport;
  var uppy = useMemo(function () {
    return buildUppy !== null ? buildUppy({
      meta: meta,
      allowMultipleUploadBatches: allowMultipleUploads,
      restrictions: {
        maxNumberOfFiles: maxNumberOfFiles,
        allowedFileTypes: allowedFileTypes
      },
      autoProceed: autoProceed,
      debug: debug
    }) : null;
  }, [buildUppy, meta, allowMultipleUploads, debug, maxNumberOfFiles, allowedFileTypes, autoProceed]);
  useEffect(function () {
    if (uppy === null) {
      return function () {};
    }
    var onUppyComplete = function onUppyComplete(response) {
      var _response$successful = response.successful,
        successful = _response$successful === void 0 ? [] : _response$successful,
        _response$failed = response.failed,
        failed = _response$failed === void 0 ? null : _response$failed;
      var finalSuccessful = transport === 'transloadit' ? getTransloaditMediasFromResponse(response) : successful;
      if (onComplete !== null) {
        onComplete(finalSuccessful);
      }
      if (onFail !== null) {
        onFail(failed);
      }
    };
    uppy.on('complete', onUppyComplete);
    return function () {
      uppy.off('complete', onUppyComplete);
    };
  }, [uppy, transport, onComplete]);
  useEffect(function () {
    if (uppy === null) {
      return function () {};
    }
    var onUpload = function onUpload(_ref6) {
      var _ref6$fileIDs = _ref6.fileIDs,
        ids = _ref6$fileIDs === void 0 ? [] : _ref6$fileIDs;
      ids.forEach(function (id) {
        var file = uppy.getFile(id);
        // console.log('file', id, file);
        var newName = null;
        if (withUUID) {
          newName = getFileNameWithUUID(file);
        } else {
          newName = getFileName(file);
        }
        if (newName !== null) {
          uppy.setFileMeta(id, {
            name: newName
          });
        }
      });
    };
    uppy.on('upload', onUpload);
    return function () {
      uppy.off('upload', onUpload);
    };
  }, [uppy]);
  useEffect(function () {
    return function () {
      if (uppy !== null) {
        uppy.close();
      }
    };
  }, [uppy]);
  return uppy;
};
var propTypes = {
  id: PropTypes.string,
  children: PropTypes.node.isRequired,
  transport: PropTypes.oneOf(['xhr', 'transloadit', 'tus']),
  locale: PropTypes.string,
  sources: PropTypes.arrayOf(PropTypes.oneOf(['webcam', 'facebook', 'instagram', 'dropbox', 'google-drive'])),
  transloadit: PropTypes.shape({
    key: PropTypes.string.isRequired,
    templateId: PropTypes.string,
    waitForEncoding: PropTypes.bool
  }),
  companion: PropTypes.shape({
    url: PropTypes.string.isRequired,
    allowedHosts: PropTypes.string.isRequired
  }),
  tus: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({
    endpoint: PropTypes.string.isRequired
  })]),
  xhr: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({
    endpoint: PropTypes.string.isRequired
  })])
};
var defaultProps = {
  id: 'uppy',
  transport: null,
  locale: null,
  sources: null,
  transloadit: null,
  companion: null,
  tus: null,
  xhr: null
};
var UppyProvider = function UppyProvider(_ref7) {
  var id = _ref7.id,
    children = _ref7.children,
    providedTransport = _ref7.transport,
    providedLocale = _ref7.locale,
    providedSources = _ref7.sources,
    providedTransloadit = _ref7.transloadit,
    providedCompanion = _ref7.companion,
    providedTus = _ref7.tus,
    providedXhr = _ref7.xhr;
  var _useIntl = useIntl(),
    intlLocale = _useIntl.locale;
  var _ref8 = useContext(UppyContext) || {},
    _ref8$transport = _ref8.transport,
    contextTransport = _ref8$transport === void 0 ? null : _ref8$transport,
    _ref8$locale = _ref8.locale,
    contextLocale = _ref8$locale === void 0 ? null : _ref8$locale,
    _ref8$sources = _ref8.sources,
    contextSources = _ref8$sources === void 0 ? null : _ref8$sources,
    _ref8$transloadit = _ref8.transloadit,
    contextTransloadit = _ref8$transloadit === void 0 ? null : _ref8$transloadit,
    _ref8$companion = _ref8.companion,
    contextCompanion = _ref8$companion === void 0 ? null : _ref8$companion,
    _ref8$tus = _ref8.tus,
    contextTus = _ref8$tus === void 0 ? null : _ref8$tus,
    _ref8$xhr = _ref8.xhr,
    contextXhr = _ref8$xhr === void 0 ? null : _ref8$xhr;
  var transport = providedTransport || contextTransport || 'xhr';
  var locale = providedLocale || contextLocale || intlLocale;
  var sources = providedSources || contextSources || ['webcam', 'facebook', 'instagram', 'dropbox', 'google-drive'];
  var transloadit = providedTransloadit || contextTransloadit;
  var companion = providedCompanion || contextCompanion;
  var tus = providedTus || contextTus;
  var xhr = providedXhr || contextXhr;
  var Uppy = useUppyCore();
  var uppyTransport = useUppyTransport(transport);
  var uppySources = useUppySources(sources);
  var uppyLocale = useUppyLocale(locale || intlLocale);

  // console.log('Init uppy', Uppy, uppyTransport, uppySources, uppyLocale);

  var buildUppy = useMemo(function () {
    if (Uppy === null || uppyTransport === null || uppySources === null || uppyLocale === null) {
      return null;
    }
    return function () {
      var opts = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      var _ref9 = opts || {},
        _ref9$sources = _ref9.sources,
        customSources = _ref9$sources === void 0 ? sources : _ref9$sources,
        uppyOpts = _objectWithoutProperties(_ref9, _excluded);
      // console.log('Uppy opts buildUppy', opts);
      var newUppy = new Uppy(_objectSpread({
        id: id,
        locale: uppyLocale
      }, uppyOpts));
      if (transport === 'transloadit') {
        var key = transloadit.key,
          templateId = transloadit.templateId,
          _transloadit$waitForE = transloadit.waitForEncoding,
          waitForEncoding = _transloadit$waitForE === void 0 ? true : _transloadit$waitForE,
          transloaditOpts = _objectWithoutProperties(transloadit, _excluded2);
        newUppy.use(uppyTransport, {
          params: _objectSpread({
            auth: {
              key: key
            },
            template_id: templateId
          }, transloaditOpts),
          waitForEncoding: waitForEncoding
        });
      } else if (transport === 'tus') {
        newUppy.use(uppyTransport, _objectSpread({
          endpoint: '/tus',
          // resume: true, obsolete
          retryDelays: [0, 1000, 3000, 5000]
        }, tus));
      } else if (transport === 'xhr') {
        newUppy.use(uppyTransport, _objectSpread({
          endpoint: isString(xhr) ? xhr : '/upload'
        }, isObject(xhr) ? xhr : null));
      }
      if (transport === 'transloadit' || companion !== null) {
        return customSources.reduce(function (currentUppy, sourceId) {
          var source = uppySources[sourceId] || null;
          if (source === null) {
            return currentUppy;
          }
          var _ref10 = companion || {
              url: uppyTransport.COMPANION || null,
              allowedHosts: uppyTransport.COMPANION_PATTERN || null
            },
            companionUrl = _ref10.url,
            companionAllowedHosts = _ref10.allowedHosts;
          return newUppy.use(source, {
            id: sourceId,
            companionUrl: companionUrl,
            companionAllowedHosts: companionAllowedHosts
          });
        }, newUppy);
      }
      return newUppy;
    };
  }, [id, Uppy, uppyLocale, uppyTransport, uppySources, transport, sources, transloadit, companion, tus, xhr]);
  var value = useMemo(function () {
    return {
      id: id,
      transport: transport,
      locale: locale,
      sources: sources,
      transloadit: transloadit,
      companion: companion,
      tus: tus,
      xhr: xhr,
      Uppy: Uppy,
      uppyTransport: uppyTransport,
      uppySources: uppySources,
      uppyLocale: uppyLocale,
      buildUppy: buildUppy
    };
  }, [id, transport, locale, sources, transloadit, companion, tus, xhr, Uppy, uppyTransport, uppySources, uppyLocale, buildUppy]);
  return /*#__PURE__*/React.createElement(UppyContext.Provider, {
    value: value
  }, children);
};
UppyProvider.propTypes = propTypes;
UppyProvider.defaultProps = defaultProps;

export { UppyContext, UppyProvider, useUppy, useUppyConfig, useUppyCore, useUppyLocale, useUppySources, useUppyTransport };
